import { create } from "zustand";
import type { AnalysisResult, FileOrderAssessment, FilterState } from "../engine/types";
import type { RecoveryReview } from "../engine/review";

type UiState = {
  analysis: AnalysisResult | null;
  review: RecoveryReview | null;
  filter: FilterState;
  selectedRecordId: string | null;
  workerStep: string | null;
  fileOrderAssessment: FileOrderAssessment | null;
  /**
   * The two input files' text, kept for the session that ran them.
   *
   * Only so a session bundle can embed them for a colleague. Absent on a run
   * restored from cache — the cache holds the analysis, not the megabytes of
   * source JSON — which is why the embed option is offered conditionally rather
   * than producing a bundle silently missing what it promised. Cleared by
   * `reset`, so it does not outlive the run it belongs to.
   */
  inputTexts: Array<{ role: "reference" | "candidate"; fileName: string; text: string }> | null;
  setInputTexts: (texts: UiState["inputTexts"]) => void;
  setAnalysis: (analysis: AnalysisResult | null) => void;
  setReview: (review: RecoveryReview | null) => void;
  setFilter: (update: Partial<FilterState>) => void;
  resetFilter: () => void;
  setSelectedRecordId: (recordId: string | null) => void;
  setWorkerStep: (step: string | null) => void;
  setFileOrderAssessment: (assessment: FileOrderAssessment | null) => void;
  reset: () => void;
};

const defaultFilter: FilterState = {
  status: "all",
  field: null,
  kind: "all",
  severity: "all",
  qualityIssueId: null,
  documentState: "all",
  onlyQualityFailures: false,
  hasEmptyLatest: false,
  search: ""
};

export const useUiStore = create<UiState>((set) => ({
  analysis: null,
  review: null,
  filter: defaultFilter,
  selectedRecordId: null,
  workerStep: null,
  fileOrderAssessment: null,
  inputTexts: null,
  setInputTexts: (inputTexts) => set({ inputTexts }),
  setAnalysis: (analysis) => set({ analysis }),
  setReview: (review) => set({ review }),
  setFilter: (update) => set((state) => ({ filter: { ...state.filter, ...update } })),
  resetFilter: () => set({ filter: defaultFilter }),
  setSelectedRecordId: (selectedRecordId) => set({ selectedRecordId }),
  setWorkerStep: (workerStep) => set({ workerStep }),
  setFileOrderAssessment: (fileOrderAssessment) => set({ fileOrderAssessment }),
  reset: () =>
    set({
      analysis: null,
      review: null,
      filter: defaultFilter,
      selectedRecordId: null,
      workerStep: null,
      fileOrderAssessment: null,
      inputTexts: null
    })
}));
